import {
  AccountBalance,
  ArrowDropDown,
  CalendarMonth,
  Calculate,
  CloudUpload,
  ContentCopy,
  DoDisturbOn,
  ListAlt,
  LocalBar,
  LockOpenOutlined,
  LockOutlined,
  ReceiptLong,
  Restaurant,
  SaveOutlined,
  Extension,
  Favorite,
  Warning,
} from '@mui/icons-material';
import {
  Alert,
  AlertColor,
  Box,
  Button,
  ButtonGroup,
  debounce,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import dayjs from 'dayjs';
import strings from 'common/Translation/Translate';
import PrimaryButton from 'components/Button/PrimaryButton';
import DrawerMenu from 'components/DrawerMenu';
import Form, { FormDataModel } from 'components/Form';
import Notification from 'components/Notification';
import PageLoader from 'components/PageLoader';
import ConfirmModal from 'components/ConfirmModal';
import UnsavedChangesModal from 'components/UnsavedChangesModal';
import { ReportInsightBanner } from 'screens/Authenticated/Reports/ReportCardHeader';
import { RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { CostingComponent } from 'screens/Authenticated/NewBooking/CostingComponent';
import axiosInstance from 'utils/axios';
import Config from 'utils/Config';
import Currency, { getCurrencySymbol } from 'utils/hooks/getCurrency';
import { identifiers } from 'utils/constants/identifiers';
import { privileges } from 'utils/constants/privileges';
import { URLS } from 'utils/constants/urls';
import { getPmsDisplayName, isOperaIntegration, supportsPmsBillPost } from 'utils/constants/data';
import { Privilege, selectIntegration, User } from 'utils/redux/reducer/authentication-slice';
import { fetchFromStorage } from 'utils/storage';
import { getPricePackageName } from 'utils/pricePackage';
import {
  createDropdownData,
  downloadExportFile,
  fetchErrorFields,
  fetchIdsFromSelectedDropdownValues,
  getDrawerWidth,
  removeErrorFieldsFromValues,
  ScrollTop,
} from 'utils/validators/HelperFunctions';
import { ActualRevenueForm } from 'screens/Authenticated/Actualization/ActualRevenue/ActualRevenueForm';
import { AddEditBookingAddOn } from './AddEditBookingAddOn/AddEditBookingAddOn';
import { AddEditDiscount } from './AddEditDiscount/AddEditDiscount';
import { VenueChargesTable } from './VenueCharges/VenueChargesTable';
import { AddEditOtherItems } from './AddEditOtherItems/AddEditOtherItems';
import { regex, Validators } from 'utils/validators/Validators';
import SendEmail from './SendEmail';
import { EInvoiceErrorsCard } from 'screens/Authenticated/Actualization/ActualRevenue/EInvoiceErrorsCard';
import {
  extractIrnFromResponse,
  parseAxiosClearTaxError,
  parseClearTaxIrnErrors,
  parseClearTaxValidateErrors,
} from 'screens/Authenticated/Actualization/ActualRevenue/clearTaxResponseErrors';
import {
  validateClearTaxPosting,
  type ClearTaxValidationOptions,
} from 'screens/Authenticated/Actualization/ActualRevenue/clearTaxValidation';

interface ActualRevenueProps {
  bookingLineItem: any;
  onSuccess: () => void;
  isWithinActualizationWindow: boolean;
  isPastActualizationWindow: boolean;
}

const floatRangeMessage = (value: string, min: number, max: number): string => {
  if (value === '' || value === '-' || value === '+') return '';
  const result = Validators.validateFloat(value, '', min, max);
  return result && result.error ? String(result.message) : '';
};

const dropdownValue = (value: any) =>
  value && typeof value === 'object' && 'value' in value ? value.value : value ?? null;

const idOrNull = (value: any) => {
  const raw = dropdownValue(value);
  return raw === '' || raw === undefined ? null : raw;
};

const ACTUALIZATION_DISPLAY_FIELDS = [
  'billableGuests',
  'excessGuestCount',
  'pricePerPerson',
  'event_details_section',
  'settlement_section',
  'tds_field',
  'round_off_field',
  'venue_charges_section',
  'venue_charges_table',
];

const stripActualizationDisplayFields = (data: any) => {
  const cleaned = { ...data };
  ACTUALIZATION_DISPLAY_FIELDS.forEach(key => {
    delete cleaned[key];
    delete cleaned[`${key}Error`];
  });
  return cleaned;
};

const GUEST_CHARGES_CATEGORY = 'GUEST_CHARGES';

const mapPreviewAddOn = (item: any) => ({
  id: item.id ?? null,
  addOnId: dropdownValue(item.bookedAddOnId),
  price: item.price,
  quantity: item.quantity,
  actualQuantity: item.actualQuantity,
  discountType: item.discount === '' ? null : item.discountType,
  discount: item.discount === '' ? null : item.discount,
  taxGroupId: idOrNull(
    item?.selectedTaxGroup?.value ?? dropdownValue(item?.taxGroupId) ?? dropdownValue(item?.taxGroup),
  ),
});

const mapPreviewOtherItem = (item: any) => ({
  id: item.id ?? null,
  name: item.name,
  type: dropdownValue(item.type),
  category: dropdownValue(item.category),
  hsnCode: item.hsnCode,
  price: item.price,
  quantity: item.quantity,
  discountType: item.discount === '' ? null : item.discountType,
  discount: item.discount === '' ? null : item.discount,
  taxGroupId: idOrNull(dropdownValue(item?.taxGroupId) ?? item?.selectedTaxGroup?.value),
});

const mapPreviewAngItem = (item: any) => ({
  id: item.id ?? null,
  name: item.name,
  type: dropdownValue(item.type),
  category: dropdownValue(item.category),
  hsnCode: item.hsnCode,
  price: item.price,
  quantity: item.quantity,
  discountType: item.discount === '' ? null : item.discountType,
  discount: item.discount === '' ? null : item.discount,
});

const mapPreviewDiscount = (item: any) => ({
  id: item.id ?? null,
  description: item.description,
  discountType: dropdownValue(item.discountType),
  price: item.price,
  addOnCategory: dropdownValue(item.addOnCategory),
});

const ActualRevenue = ({ bookingLineItem, onSuccess, isWithinActualizationWindow }: ActualRevenueProps) => {
  const { id } = useParams();
  const theme = useTheme();
  const userPrivileges = useSelector(Privilege);
  const user = useSelector(User) as { role?: { value?: string } } | null;
  const integration = useSelector(selectIntegration);
  const isBbrAdmin = user?.role?.value === 'ROLE_BBR_ADMIN';
  const token = fetchFromStorage(identifiers.access_token);
  const serviceChargeDropdown = JSON.parse(fetchFromStorage('serviceChargeDropdown') as any);
  const bookedServiceChargePercent = Number(bookingLineItem?.serviceCharge) || 0;
  const screenSizeDownSm = useMediaQuery(theme.breakpoints.down('sm'));
  const screenSizeDownMd = useMediaQuery(theme.breakpoints.down('md'));
  const screenSizeUpLg = useMediaQuery(theme.breakpoints.up('lg'));
  let formRef: RefObject<Form | null | undefined> = useRef();
  /** Sync guard so a second Save click in the same tick cannot start another PUT. */
  const savingRef = useRef(false);
  const [hasError, setHasError] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [noGuest, setNoGuest] = useState<number>(0);
  const [isTransferToRoom, setIsTransferToRoom] = useState<boolean>(false);
  const [bookedAddOns, setBookedAddOns] = useState([]);
  const [otherItems, setOtherItems] = useState([]);
  const [angItems, setAngItems] = useState([]);
  const [discounts, setDiscounts] = useState([]);
  const [serviceCharges, setServiceCharges] = useState<any>([]);
  const [costingDrawerPageResponse, setCostingDrawerPageResponse] = useState<any>({});
  const [updateReason, setUpdateReason] = useState<any>('');
  const [updateReasonError, setUpdateReasonError] = useState<any>('');
  const [openEmailDrawer, setOpenEmailDrawer] = useState<boolean>(false);
  const [roomNo, setRoomNo] = useState('');
  const [isBillPosted, setIsBillPosted] = useState<boolean>(false);
  const [addOnCategories, setAddOnCategories] = useState<any>([]);
  const discountCategories = useMemo(() => {
    if (bookingLineItem?.hourlyPricing) return addOnCategories;
    const packageName = getPricePackageName(bookingLineItem, '').trim();
    const guestChargesLabel = packageName
      ? `${strings.actualization_discount_category_guest_charges} (${packageName})`
      : strings.actualization_discount_category_guest_charges;
    return [{ value: GUEST_CHARGES_CATEGORY, label: guestChargesLabel }, ...(addOnCategories || [])];
  }, [addOnCategories, bookingLineItem]);
  const [openVoidBillDialog, setOpenVoidBillDialog] = useState<boolean>(false);
  const [finalizeModalOpen, setFinalizeModalOpen] = useState(false);
  const [reopenModalOpen, setReopenModalOpen] = useState(false);
  const [finalizingActualization, setFinalizingActualization] = useState(false);
  const [reopeningActualization, setReopeningActualization] = useState(false);
  const [cancelIrnDialogOpen, setCancelIrnDialogOpen] = useState(false);
  const [cancelIrnReason, setCancelIrnReason] = useState('4');
  const [cancelIrnRemarks, setCancelIrnRemarks] = useState('');
  const [cancellingIrn, setCancellingIrn] = useState(false);
  const [generateIrnModalOpen, setGenerateIrnModalOpen] = useState(false);
  const [generatingIrn, setGeneratingIrn] = useState(false);
  const [postBillModalOpen, setPostBillModalOpen] = useState(false);
  const [postingBill, setPostingBill] = useState(false);
  const [voidingBill, setVoidingBill] = useState(false);
  const [billViewAnchorEl, setBillViewAnchorEl] = useState<HTMLElement | null>(null);
  const [eInvoiceErrors, setEInvoiceErrors] = useState<{ title: string; errors: string[] } | null>(null);
  const [eInvoiceValidated, setEInvoiceValidated] = useState(false);

  const [roundOffInput, setRoundOffInput] = useState<string>('');
  const [tdsPercentage, setTdsPercentage] = useState<string>('');
  const [excessGuestRateLive, setExcessGuestRateLive] = useState<number>(0);
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const [preview, setPreview] = useState<any>(null);
  const initialLoadingRef = useRef<boolean>(false);
  const [unsavedDialog, setUnsavedDialog] = useState<{
    open: boolean;
    action: (() => void | Promise<void>) | null;
  }>({ open: false, action: null });
  const [unsavedSaving, setUnsavedSaving] = useState(false);

  const markDirty = useCallback(() => {
    if (!initialLoadingRef.current) {
      setIsDirty(true);
      setEInvoiceValidated(false);
    }
  }, []);
  const [revenueOwnerList, setRevenueOwnerList] = useState<any[]>([]);
  const hasSplitRevenuePrivilege = userPrivileges?.includes(privileges.splitRevenue);
  const [message, setMessage] = useState<{
    display: boolean;
    severity: AlertColor | null;
    message: any;
  }>({
    display: false,
    severity: null,
    message: '',
  });
  const [pageResponse, setPageResponse] = useState<any>({});
  const canPostClearTax = userPrivileges?.includes(privileges.post_clear_tax);

  const formSnapshotRef = useRef<any>(null);
  const previewSeqRef = useRef(0);
  const previewInputRef = useRef({
    bookedAddOns,
    otherItems,
    discounts,
    angItems,
    roundOffInput,
    tdsPercentage,
    excessGuestRateLive,
    pageResponse,
  });
  previewInputRef.current = {
    bookedAddOns,
    otherItems,
    discounts,
    angItems,
    roundOffInput,
    tdsPercentage,
    excessGuestRateLive,
    pageResponse,
  };

  const buildPreviewRequest = useCallback((formData?: any) => {
    const snapshot = previewInputRef.current;
    const source = formData || formSnapshotRef.current || snapshot.pageResponse || {};
    const cleanedData = stripActualizationDisplayFields(removeErrorFieldsFromValues(source));
    let parsedRoundOff = 0;
    if (snapshot.roundOffInput !== '' && snapshot.roundOffInput !== '-' && snapshot.roundOffInput !== '+') {
      parsedRoundOff = Number(snapshot.roundOffInput) || 0;
    }
    const excessRateRaw = cleanedData.excessGuestRate ?? snapshot.excessGuestRateLive;
    const excessGuestRate =
      excessRateRaw === '' || excessRateRaw === null || excessRateRaw === undefined ? null : parseFloat(excessRateRaw);
    const scRaw = cleanedData.serviceChargePercentage ?? snapshot.pageResponse?.serviceChargePercentage;
    const serviceChargePercentage = scRaw === '' || scRaw === null || scRaw === undefined ? 0 : parseFloat(scRaw);

    return {
      guaranteedGuests: Number(cleanedData.guaranteedGuests ?? snapshot.pageResponse?.guaranteedGuests) || 0,
      hallRental: Number(cleanedData.hallRental ?? snapshot.pageResponse?.hallRental) || 0,
      serviceChargePercentage: Number.isFinite(serviceChargePercentage) ? serviceChargePercentage : 0,
      tdsPercentage: snapshot.tdsPercentage ? parseFloat(snapshot.tdsPercentage) : null,
      excessGuestRate: Number.isFinite(Number(excessGuestRate)) ? excessGuestRate : null,
      roundOff: parsedRoundOff,
      bookedAddOns: (snapshot.bookedAddOns || []).map(mapPreviewAddOn),
      otherItems: (snapshot.otherItems || []).map(mapPreviewOtherItem),
      angDetails: (snapshot.angItems || [])
        .filter(
          (item: any) => item?.name && dropdownValue(item.type) && dropdownValue(item.category) && item?.price != null,
        )
        .map(mapPreviewAngItem),
      discounts: (snapshot.discounts || [])
        .filter((item: any) => item?.description && item?.price != null)
        .map(mapPreviewDiscount),
      roomNumber: cleanedData.roomNumber ?? null,
      roomReservationName: cleanedData.roomReservationName ?? null,
      roomReservationId: cleanedData.roomReservationId ?? null,
    };
  }, []);

  const requestPreview = useMemo(
    () =>
      debounce(() => {
        if (!id) return;
        const payload = buildPreviewRequest(formSnapshotRef.current);
        const seq = ++previewSeqRef.current;
        axiosInstance
          .post(URLS.previewActualization(id), payload)
          .then(({ data }) => {
            if (seq === previewSeqRef.current) {
              setPreview(data);
            }
          })
          .catch(() => {
            /* preview is best-effort; save validation still applies */
          });
      }, 600),
    [id, buildPreviewRequest],
  );

  useEffect(() => () => requestPreview.clear(), [requestPreview]);

  useEffect(() => {
    if (!id) return;
    requestPreview();
  }, [
    id,
    bookedAddOns,
    otherItems,
    discounts,
    angItems,
    roundOffInput,
    tdsPercentage,
    pageResponse?.guaranteedGuests,
    requestPreview,
  ]);

  const getBillableGuests = (actualGuests: any, persistedBillableGuests?: any) => {
    const bookingGuests = Number(bookingLineItem?.guaranteedGuests) || 0;
    const actual = Number(actualGuests) || 0;
    const persisted = Number(persistedBillableGuests);
    return Number.isFinite(persisted) && persisted > 0 ? persisted : Math.max(bookingGuests, actual);
  };

  const getRevenueOwners = useCallback(async (branchId: number | null = null) => {
    try {
      const url = URLS.revenueOwners(branchId);
      const { data } = await axiosInstance.get(url);
      const revenueOwners = Array.isArray(data)
        ? data.map((item: any) => ({
            label: item.name || item.label || '',
            value: item.id || item.value || '',
          }))
        : [];
      setRevenueOwnerList(revenueOwners);
    } catch (error: any) {
      handleCatch(error);
    }
  }, []);
  const getInitialResponse = useCallback(async () => {
    initialLoadingRef.current = true;
    try {
      const { data } = await axiosInstance.get(URLS.getActualRevenueByBookingId(id));
      const isCompany = data.corporateBooking === true || data.corporateBooking === 'true';
      const branchId = isCompany && data.companyBranch ? data.companyBranch : null;
      await getRevenueOwners(branchId);

      setPageResponse({
        ...data,
        billableGuests: getBillableGuests(data?.guaranteedGuests, data?.billableGuests),
        roomNumber: data.roomNumber !== null ? data.roomNumber : '',
        serviceCharge: data.serviceCharge === null ? null : data.serviceCharge?.value,
        isTransferToRoom: data.roomNumber !== null && data.roomNumber !== '',
        revenueOwners: data.revenueOwners || null,
      });
      setBookedAddOns(
        data?.bookedAddOns?.map((item: any) => ({
          ...item,
          taxGroupId: item?.selectedTaxGroup?.value ?? item?.taxGroupId ?? '',
        })) ?? [],
      );
      setOtherItems(
        data?.otherItems?.map((item: any) => ({
          ...item,
          taxGroupId: item?.selectedTaxGroup?.value ?? item?.taxGroupId ?? '',
        })) ?? [],
      );
      setAngItems(data?.angDetails);
      setNoGuest(getBillableGuests(data?.guaranteedGuests, data?.billableGuests));
      setExcessGuestRateLive(Number(data?.excessGuestRate) || 0);
      setDiscounts(data?.discounts);
      setIsTransferToRoom(data.roomNumber !== null && data.roomNumber !== '');
      setIsBillPosted(data.isBillPosted ?? false);
      const a = createDropdownData(data?.serviceCharges, ['id', 'name']) || [];
      setServiceCharges(a);
      const incomingRoundOff = data?.roundOff;
      if (incomingRoundOff === 0 || incomingRoundOff === null || incomingRoundOff === undefined) {
        setRoundOffInput('');
      } else {
        setRoundOffInput(String(incomingRoundOff));
      }

      const incomingTdsPercentage = data?.tdsPercentage;
      if (incomingTdsPercentage === 0 || incomingTdsPercentage === null || incomingTdsPercentage === undefined) {
        setTdsPercentage('');
      } else {
        setTdsPercentage(String(incomingTdsPercentage));
      }
      setIsDirty(false);
      setEInvoiceValidated(false);
    } catch (error: any) {
      handleCatch(error);
    } finally {
      initialLoadingRef.current = false;
    }
  }, [id, hasSplitRevenuePrivilege, getRevenueOwners]);

  const getAddOnCategories = async () => {
    const { data, status } = await axiosInstance.get(URLS.getAddOnCategoriesDropdown);
    let newArray;
    if (status === 200 && data.length > 0) {
      newArray = data.map((item: any) => {
        return { value: item.id, label: item.name, hsnCode: item.hsnCode };
      });
    }
    setAddOnCategories(newArray);
  };

  useEffect(() => {
    (async () => {
      if (id) {
        await getAddOnCategories();
        await getInitialResponse();
      }
    })();
  }, [getInitialResponse, id]);

  const handleCatch = (error: any) => {
    setMessage({
      display: true,
      severity: 'error',
      message:
        error.response.data?.error_description ??
        error.response.data.message ??
        error.message ??
        'Something went wrong',
    });
  };

  const handleAddEdit = async (): Promise<boolean> => {
    if (pageResponse.irnNumber || isBillPosted) {
      return false;
    }
    if (savingRef.current || loading) return false;
    savingRef.current = true;
    try {
      const { getFormData } = formRef.current as {
        getFormData: () => { formData: FormDataModel; isFormValid: boolean };
      };
      const { formData, isFormValid } = getFormData();
      let a = isFormValid;
      if (isTransferToRoom && formData.roomNumber === '') {
        formRef.current?.handleChange('', 'roomNumber', { error: true, message: strings.requiredText });
        a = false;
      }

      const cleanedData = stripActualizationDisplayFields(removeErrorFieldsFromValues(formData));
      if (a) {
        if (updateReason === '') {
          setUpdateReasonError(true);
          setMessage({
            display: true,
            severity: 'error',
            message: `${strings.booking_details_actual_revenue_update_reason} ${strings.requiredText ?? 'is required'}`,
          });
          return false;
        }
        // parse roundOffInput to number for submission
        let parsedRoundOff = 0;
        if (roundOffInput !== '' && roundOffInput !== '-' && roundOffInput !== '+') {
          parsedRoundOff = Number(roundOffInput);
        }

        if (roundOffError) {
          setMessage({
            display: true,
            severity: 'error',
            message: roundOffError,
          });
          return false;
        }
        setLoading(true);

        const excessRateRaw = (cleanedData as any).excessGuestRate ?? excessGuestRateLive;
        const excessGuestRate =
          excessRateRaw === '' || excessRateRaw === null || excessRateRaw === undefined
            ? null
            : parseFloat(excessRateRaw);

        const requestBody = {
          ...cleanedData,
          excessGuestRate,
          roundOff: parsedRoundOff,
          bookedAddOns: bookedAddOns.map(mapPreviewAddOn),
          otherItems: otherItems?.length > 0 ? otherItems.map(mapPreviewOtherItem) : [],
          angDetails: angItems?.length > 0 ? angItems.map(mapPreviewAngItem) : [],
          discounts: discounts.map(mapPreviewDiscount),
          roomNumber: cleanedData.roomNumber ?? null,
          roomReservationName: cleanedData.roomReservationName ?? null,
          roomReservationId: cleanedData.roomReservationId ?? null,
          updateReason: updateReason,
          tdsPercentage: tdsPercentage ? parseFloat(tdsPercentage) : null,
          revenueOwners: fetchIdsFromSelectedDropdownValues(cleanedData.revenueOwners || []),
        };

        const { status } = await axiosInstance.put(URLS.getActualRevenueByBookingId(id), requestBody);
        if (status === 200 || status === 201) {
          setUpdateReason('');
          setRoundOffInput('');
          setTdsPercentage('');
          setLoading(false);
          ScrollTop(0);
          setMessage({
            display: true,
            severity: 'success',
            message: strings.actualRevenueAddSuccessText,
          });
          onSuccess();
          await getInitialResponse();
          setHasError(false);
          setIsDirty(false);
          return true;
        }
        return false;
      } else {
        setMessage({
          display: true,
          severity: 'error',
          message: `${strings.required_error_prefix_message} ${fetchErrorFields(
            formData,
            ActualRevenueForm(
              strings,
              isWithinActualizationWindow,
              isTransferToRoom,
              serviceChargeDropdown,
              serviceCharges,
              hasSplitRevenuePrivilege,
              revenueOwnerList,
              bookedServiceChargePercent,
              excessGuestCount,
              extraGuestRateText,
            ),
          ).join(', ')}`,
        });
        setHasError(true);
        return false;
      }
      return false;
    } catch (error: any) {
      handleCatch(error);
      return false;
    } finally {
      savingRef.current = false;
      setLoading(false);
    }
  };

  const runWithSaveIfDirty = (action: () => void | Promise<void>) => {
    if (isDirty) {
      setUnsavedDialog({ open: true, action });
      return;
    }
    void action();
  };

  const closeUnsavedDialog = () => setUnsavedDialog({ open: false, action: null });

  const handleSaveAndContinue = async () => {
    const action = unsavedDialog.action;
    try {
      setUnsavedSaving(true);
      const ok = await handleAddEdit();
      if (ok) {
        closeUnsavedDialog();
        if (action) {
          await action();
        }
      }
    } finally {
      setUnsavedSaving(false);
    }
  };

  const handleDiscardAndContinue = async () => {
    const action = unsavedDialog.action;
    closeUnsavedDialog();
    setUpdateReason('');
    setUpdateReasonError(false);
    await getInitialResponse();
    if (action) {
      await action();
    }
  };

  const fetchRoomReservations = useCallback(
    debounce(async (roomNo: string) => {
      try {
        setLoading(true);
        const { status, data } = await axiosInstance.post(URLS.fetchReservations(roomNo));
        if ([200, 201].includes(status)) {
          setPageResponse((prev: any) => ({
            ...prev,
            roomNumber: data.roomNumber ?? roomNo,
            roomReservationId: data.roomReservationId ?? 'N/A',
            roomReservationName: data.roomReservationName ?? 'N/A',
          }));
        }
      } catch (error: any) {
        handleCatch(error);
      } finally {
        setLoading(false);
      }
    }, 1000),
    [],
  );

  useEffect(() => {
    if (roomNo) {
      fetchRoomReservations(roomNo);
    }
  }, [fetchRoomReservations, roomNo]);

  const billPost = async () => {
    if (postingBill || isBillPosted) {
      return;
    }
    try {
      setPostingBill(true);
      const { status } = await axiosInstance.post(URLS.postBill(id));

      if ([200, 201].includes(status)) {
        setMessage({
          display: true,
          severity: 'success',
          message: strings.billPostSuccessText,
        });
        setHasError(false);
        setIsBillPosted(true);
        setPostBillModalOpen(false);
        await getInitialResponse();
      }
    } catch (error: any) {
      handleCatch(error);
    } finally {
      setPostingBill(false);
    }
  };

  const handlePostBillConfirm = async () => {
    await runWithSaveIfDirty(billPost);
  };

  const handleVoidBillClick = () => {
    setOpenVoidBillDialog(true);
  };

  const voidBill = async () => {
    if (voidingBill || !isBillPosted) {
      return;
    }
    try {
      setVoidingBill(true);
      const { status } = await axiosInstance.post(URLS.voidBill(id));

      if ([200, 201].includes(status)) {
        setMessage({
          display: true,
          severity: 'success',
          message: strings.billPostSuccessText,
        });
        setHasError(false);
        setIsBillPosted(false);
        setOpenVoidBillDialog(false);
        await getInitialResponse();
      }
    } catch (error: any) {
      handleCatch(error);
    } finally {
      setVoidingBill(false);
    }
  };

  const showEInvoiceErrorCard = (title: string, errors: string[]) => {
    const unique = [...new Set(errors.map(e => e.trim()).filter(Boolean))];
    if (!unique.length) return;
    setEInvoiceErrors({ title, errors: unique });
  };

  const clearEInvoiceErrorCard = () => setEInvoiceErrors(null);

  const runClientClearTaxValidation = (options?: ClearTaxValidationOptions): boolean => {
    const validation = validateClearTaxPosting(bookingLineItem?.booking, pageResponse, options);
    if (!validation.valid) {
      showEInvoiceErrorCard(strings.clearTaxValidationTitle, validation.errors);
      return false;
    }
    return true;
  };

  const validateEInvoice = async () => {
    if (!runClientClearTaxValidation()) {
      setEInvoiceValidated(false);
      return;
    }

    try {
      setLoading(true);
      clearEInvoiceErrorCard();
      setEInvoiceValidated(false);
      const { status, data } = await axiosInstance.post(URLS.validateEInvoice(id));
      // eslint-disable-next-line no-console -- intentional debug log for ClearTax validate payload
      console.log('[Validate e-Invoice] raw response', data);

      if ([200, 201].includes(status)) {
        const errors = parseClearTaxValidateErrors(data);
        if (errors.length > 0) {
          setEInvoiceValidated(false);
          showEInvoiceErrorCard(strings.eInvoiceValidationErrorsTitle, errors);
          return;
        }
        clearEInvoiceErrorCard();
        setEInvoiceValidated(true);
        setMessage({
          display: true,
          severity: 'success',
          message: strings.validateEInvoiceSuccessText,
        });
        setHasError(false);
      }
    } catch (error: any) {
      setEInvoiceValidated(false);
      // eslint-disable-next-line no-console -- intentional debug log for ClearTax validate failures
      console.log('[Validate e-Invoice] error response', error?.response?.data ?? error);
      const errors = parseClearTaxValidateErrors(error?.response?.data);
      showEInvoiceErrorCard(
        strings.eInvoiceValidationErrorsTitle,
        errors.length > 0 ? errors : parseAxiosClearTaxError(error),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateIrnConfirm = async () => {
    if (!eInvoiceValidated) return;
    if (!runClientClearTaxValidation()) return;

    try {
      setGeneratingIrn(true);
      clearEInvoiceErrorCard();
      const { status, data } = await axiosInstance.post(URLS.generateIrn(id));

      if ([200, 201].includes(status)) {
        const errors = parseClearTaxIrnErrors(data);
        if (errors.length > 0) {
          showEInvoiceErrorCard(strings.eInvoiceGenerateErrorsTitle, errors);
          return;
        }
        if (!extractIrnFromResponse(data)) {
          showEInvoiceErrorCard(strings.eInvoiceGenerateErrorsTitle, [strings.eInvoiceNoIrnReturned]);
          return;
        }
        setMessage({
          display: true,
          severity: 'success',
          message: strings.clearTaxPostSuccessText,
        });
        setHasError(false);
        setGenerateIrnModalOpen(false);
        clearEInvoiceErrorCard();
        await getInitialResponse();
      }
    } catch (error: any) {
      showEInvoiceErrorCard(strings.eInvoiceGenerateErrorsTitle, parseAxiosClearTaxError(error));
    } finally {
      setGeneratingIrn(false);
    }
  };

  const downloadEInvoicePdf = async () => {
    try {
      setLoading(true);
      clearEInvoiceErrorCard();
      const response = await axiosInstance({
        url: `${Config.BaseURL + URLS.downloadEInvoicePdf(id)}`,
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      downloadExportFile(response, 'pdf', `e-invoice-${pageResponse.irnNumber ?? id}`);
    } catch (error: any) {
      const data = error?.response?.data;
      if (data instanceof Blob) {
        try {
          const parsed = JSON.parse(await data.text());
          showEInvoiceErrorCard(
            strings.eInvoiceDownloadErrorsTitle,
            parseAxiosClearTaxError({ response: { data: parsed } }),
          );
        } catch {
          showEInvoiceErrorCard(strings.eInvoiceDownloadErrorsTitle, parseAxiosClearTaxError(error));
        }
      } else {
        showEInvoiceErrorCard(strings.eInvoiceDownloadErrorsTitle, parseAxiosClearTaxError(error));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancelIrn = useCallback(async () => {
    if (!bookingLineItem?.id) return;
    try {
      setCancellingIrn(true);
      const { status } = await axiosInstance.post(URLS.cancelIrn(id), {
        cnlRsn: cancelIrnReason,
        cnlRem: cancelIrnRemarks || 'Cancelled',
      });
      if ([200, 201].includes(status)) {
        setMessage({ display: true, severity: 'success', message: strings.cancelIrnSuccessText });
        setCancelIrnDialogOpen(false);
        setCancelIrnReason('4');
        setCancelIrnRemarks('');
        clearEInvoiceErrorCard();
        await getInitialResponse();
      }
    } catch (error: any) {
      showEInvoiceErrorCard(strings.cancelIrnConfirmTitle, parseAxiosClearTaxError(error));
    } finally {
      setCancellingIrn(false);
    }
  }, [bookingLineItem?.id, cancelIrnReason, cancelIrnRemarks, id, getInitialResponse]);

  const handleCheckSummary = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const { data } = await axiosInstance.get(URLS.getBillingCostingSummary(id));
      setCostingDrawerPageResponse(data);
    } catch (error: any) {
      handleCatch(error);
    } finally {
      setLoading(false);
    }
  };

  const closeNotification = (value: boolean) => setMessage({ ...message, display: value });

  const handleRoundOffToNearest = () => {
    if (pageResponse.totalCost) {
      const numericValue = Number(pageResponse.totalCost) - Number(pageResponse?.roundOff ?? 0);
      if (!isNaN(numericValue)) {
        const roundedValue = Math.round(numericValue);
        const roundOffValue = roundedValue - numericValue;

        // Show user what's happening with clear message
        setMessage({
          display: true,
          severity: 'info',
          message: `Rounding ${getCurrencySymbol()}${numericValue.toFixed(
            2,
          )} to ${getCurrencySymbol()}${roundedValue.toFixed(
            2,
          )} (Round off: ${getCurrencySymbol()}${roundOffValue.toFixed(2)})`,
        });

        setRoundOffInput(roundOffValue.toFixed(2));
      }
    }
  };

  const handleFormOnChange = (field: string, value: any, formData: any) => {
    markDirty();
    formSnapshotRef.current = formData;
    requestPreview();

    if (field === 'guaranteedGuests' && value !== '') {
      const billableGuests = getBillableGuests(value);
      setNoGuest(billableGuests);
      formRef.current?.handleChange(billableGuests as any, 'billableGuests', { error: false, message: '' });
      const newBookedAddOns = bookedAddOns.map((item: any) => ({
        ...item,
        actualQuantity: item.addOnType === 'PER_QUANTITY' ? item.actualQuantity : billableGuests,
      }));
      setBookedAddOns(newBookedAddOns as any);
    }

    if (field === 'excessGuestRate') {
      const parsed = Number(value);
      setExcessGuestRateLive(Number.isFinite(parsed) ? parsed : 0);
    }

    if (field === 'isTransferToRoom') {
      setIsTransferToRoom(value);
      formRef.current?.handleChange('', 'roomNumber', { error: false, message: '' });
    }

    if (field === 'roomNumber') {
      setRoomNo(value);
    }
  };

  const billViewTypes: { value: string; label: string }[] =
    Array.isArray(pageResponse?.billViewTypes) && pageResponse.billViewTypes.length > 0
      ? pageResponse.billViewTypes
      : [{ value: 'CONSOLIDATED', label: 'Consolidated' }];
  const billDateOptions: { value: string; label: string }[] = Array.isArray(pageResponse?.billDateOptions)
    ? pageResponse.billDateOptions
    : [];
  const consolidatedBillViewType = billViewTypes.find(option => option.value === 'CONSOLIDATED') ?? {
    value: 'CONSOLIDATED',
    label: 'Consolidated',
  };
  const categoryBillViewTypes = billViewTypes.filter(option => option.value !== 'CONSOLIDATED');
  const showSplitBillDownload = categoryBillViewTypes.length > 0 || billDateOptions.length > 0;

  const getBillMenuIcon = (value: string, isDateOption = false) => {
    const iconProps = { fontSize: 'small' as const, sx: { color: 'primary.main' } };

    if (isDateOption) {
      return <CalendarMonth {...iconProps} />;
    }

    switch (value) {
      case 'CONSOLIDATED':
        return <ReceiptLong {...iconProps} />;
      case 'FOOD_AND_BEVERAGE':
        return <Restaurant {...iconProps} />;
      case 'LIQUOR':
        return <LocalBar {...iconProps} />;
      case 'ADDONS':
        return <Extension {...iconProps} />;
      case 'BANQUET':
      default:
        return <AccountBalance {...iconProps} />;
    }
  };

  const downloadInvoice = async (billViewType: string = 'CONSOLIDATED', eventDate?: string, format: string = 'PDF') => {
    const documentFormat = format === 'DOCX' ? 'DOCX' : 'PDF';
    setLoading(true);
    try {
      const response = await axiosInstance({
        url: `${Config.BaseURL + URLS.downloadActualizationPDF(id, billViewType, eventDate, documentFormat)}`,
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });

      const viewLabel = eventDate
        ? `Date - ${
            billDateOptions.find(option => option.value === eventDate)?.label ??
            dayjs(eventDate).format('ddd, DD MMM YYYY')
          }`
        : billViewTypes.find(option => option.value === billViewType)?.label ?? billViewType;
      downloadExportFile(response, documentFormat.toLowerCase(), `Bill - ${viewLabel}`);
    } catch (error: any) {
      handleCatch(error);
    } finally {
      setLoading(false);
    }
  };

  const expectedGuests = Number(pageResponse?.expectedGuests) || 0;
  const excessGuestCount = Math.max(0, Number(noGuest || 0) - expectedGuests);
  const rawPricePerPerson = Number(pageResponse?.pricePerPerson) || 0;
  const extraGuestRateValue = rawPricePerPerson + (rawPricePerPerson * (excessGuestRateLive || 0)) / 100;
  const extraGuestRateText = Currency(extraGuestRateValue);
  const roundOffError = floatRangeMessage(roundOffInput, -100, 100);
  const tdsPercentageError = floatRangeMessage(tdsPercentage, 0, 100);

  const actualizationFinalized = Boolean(pageResponse?.actualizationFinalized);
  const hasIrn = !!pageResponse.irnNumber;
  const hasPmsIntegration = supportsPmsBillPost(integration);
  const pmsDisplayName = getPmsDisplayName(integration);
  const isOperaPms = isOperaIntegration(integration);
  const canShowPmsBillActions = hasPmsIntegration && (isOperaPms || isTransferToRoom);
  const isPmsBillPosted = hasPmsIntegration && isBillPosted;
  const isPmsBillFlow = canShowPmsBillActions && !isBillPosted && !hasIrn;
  const hasBuyerGstin = String(bookingLineItem?.booking?.gst ?? '').trim().length > 0;
  const canEdit =
    !bookingLineItem.deleted && isWithinActualizationWindow && !actualizationFinalized && !hasIrn && !isPmsBillPosted;
  const canDownload = !bookingLineItem.deleted && userPrivileges?.includes(privileges.download_actualization);
  const canPostBill = userPrivileges?.includes(privileges.post_bill);
  const canVoidBill = userPrivileges?.includes(privileges.void_bill) && isBillPosted;
  const showFinalizeActualization =
    !actualizationFinalized && userPrivileges?.includes(privileges.finalize_actualization) && !!pageResponse.id;
  /** ROLE_BBR_ADMIN only — not tied to any UserPrivilege. */
  const showReopenActualization = isBbrAdmin && actualizationFinalized && !!pageResponse.id;

  // Visibility conditions for update reason and save button
  const showUpdateReason =
    !hasIrn &&
    !isPmsBillPosted &&
    !actualizationFinalized &&
    (!isBillPosted || userPrivileges?.includes(privileges.add_ang_details));
  const showSaveButton =
    !hasIrn &&
    !isPmsBillPosted &&
    !actualizationFinalized &&
    (!isBillPosted || userPrivileges?.includes(privileges.add_ang_details));
  const showSummaryButton = !!pageResponse.id;
  const showDownloadButton = canDownload && !!pageResponse.id;
  const showSendEmailButton = !!pageResponse.id;
  const showClearTaxActions = !!pageResponse.id && canPostClearTax && !isTransferToRoom;
  const showValidateEInvoiceButton = showClearTaxActions && !hasIrn && !eInvoiceValidated;
  const showGenerateIrnButton = showClearTaxActions && !hasIrn && eInvoiceValidated;
  const showDownloadEInvoiceButton = showClearTaxActions && hasIrn;
  const showCancelIrnButton = showClearTaxActions && hasIrn;
  const showPostBillButton = canPostBill && canShowPmsBillActions && !isBillPosted && !hasIrn;
  const showVoidBillButton = canVoidBill && canShowPmsBillActions && !hasIrn;

  const formatPmsMessage = (template: string) => template.replace(/\{\{pmsName\}\}/g, pmsDisplayName);

  const actualizationInfo = ((): {
    text: string;
    style: 'blue' | 'yellow' | 'success';
  } | null => {
    if (hasIrn) {
      return { text: strings.irnGeneratedSuccessMessage, style: 'success' };
    }
    if (isPmsBillPosted) {
      return { text: strings.pmsBillPostedSuccessMessage, style: 'success' };
    }
    if (isPmsBillFlow) {
      if (isOperaPms && !isTransferToRoom) {
        return { text: strings.operaBillFlowInfoMessage, style: 'blue' };
      }
      return { text: formatPmsMessage(strings.pmsBillFlowInfoMessage), style: 'blue' };
    }
    if (!isTransferToRoom && !hasBuyerGstin && canPostClearTax) {
      return { text: strings.noGstinEInvoiceMessage, style: 'blue' };
    }
    return null;
  })();

  const actionButtonSx = {
    minHeight: 36,
    borderRadius: 1,
    textTransform: 'none',
    fontWeight: 600,
    whiteSpace: 'nowrap',
    ...(screenSizeDownSm ? { width: '100%' } : { minWidth: 200 }),
    ...(screenSizeDownSm && { fontSize: 13, px: 1 }),
  } as const;
  const downloadLabel = screenSizeDownSm ? 'Download' : 'Download Invoice';

  const cleartaxAccentColor = '#08a4fc';
  const cancelIrnColor = '#9f0712';

  const generateIrnButtonSx = {
    ...actionButtonSx,
    bgcolor: '#fff',
    color: cleartaxAccentColor,
    border: `1px solid ${cleartaxAccentColor}`,
    boxShadow: 'none',
    '&:hover': {
      bgcolor: 'rgba(8, 164, 252, 0.08)',
      borderColor: cleartaxAccentColor,
    },
  } as const;

  const cancelIrnButtonSx = {
    ...actionButtonSx,
    bgcolor: '#fff',
    color: cancelIrnColor,
    border: `1px solid ${cancelIrnColor}`,
    boxShadow: 'none',
    '&:hover': {
      bgcolor: 'rgba(159, 7, 18, 0.08)',
      borderColor: cancelIrnColor,
    },
  } as const;

  const copyIrnToClipboard = useCallback(async () => {
    if (!pageResponse.irnNumber) return;
    try {
      await navigator.clipboard.writeText(pageResponse.irnNumber);
      setMessage({ display: true, severity: 'success', message: strings.irnCopiedText });
    } catch {
      setMessage({ display: true, severity: 'error', message: strings.tryAgainMessage });
    }
  }, [pageResponse.irnNumber]);

  const handleFinalizeActualization = useCallback(async () => {
    if (!bookingLineItem?.id) return;
    try {
      setFinalizingActualization(true);
      await axiosInstance.post(URLS.finalizeActualization(bookingLineItem.id));
      setFinalizeModalOpen(false);
      setPageResponse((prev: any) => ({ ...prev, actualizationFinalized: true }));
      await getInitialResponse();
      onSuccess();
    } catch (error: any) {
      handleCatch(error);
    } finally {
      setFinalizingActualization(false);
    }
  }, [bookingLineItem?.id, getInitialResponse, onSuccess]);

  const handleReopenActualization = useCallback(async () => {
    if (!bookingLineItem?.id) return;
    try {
      setReopeningActualization(true);
      await axiosInstance.post(URLS.reopenActualization(bookingLineItem.id));
      setReopenModalOpen(false);
      setPageResponse((prev: any) => ({ ...prev, actualizationFinalized: false }));
      await getInitialResponse();
      onSuccess();
    } catch (error: any) {
      handleCatch(error);
    } finally {
      setReopeningActualization(false);
    }
  }, [bookingLineItem?.id, getInitialResponse, onSuccess]);

  const tdsField = (
    <TextField
      label={strings.actualization_tds_percentage}
      variant="outlined"
      size="small"
      type="text"
      inputProps={{ inputMode: 'decimal', pattern: '^\\d{0,3}(?:\\.\\d*)?$' }}
      value={tdsPercentage}
      onChange={(e: any) => {
        const val = e.target.value;
        if (val === '' || /^\d{0,3}(?:\.\d*)?$/.test(val)) {
          setTdsPercentage(val);
          markDirty();
        }
      }}
      error={!!tdsPercentageError}
      helperText={tdsPercentageError}
      fullWidth
      disabled={!canEdit || isBillPosted || hasIrn}
      sx={{ pr: 2 }}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end" sx={{ '& .MuiTypography-root': { fontSize: '0.875rem' } }}>
            <Typography color="text.secondary" component="span">
              %
            </Typography>
          </InputAdornment>
        ),
      }}
    />
  );

  const roundOffField = (
    <TextField
      label={strings.actualization_round_off}
      variant="outlined"
      size="small"
      type="text"
      inputProps={{ inputMode: 'decimal', pattern: '^[-+]?\\d{0,3}(?:\\.\\d*)?$' }}
      value={roundOffInput}
      onChange={(e: any) => {
        const val = e.target.value;
        if (val === '' || val === '-' || val === '+' || /^[-+]?\d{0,3}(?:\.\d*)?$/.test(val)) {
          setRoundOffInput(val);
          markDirty();
        }
      }}
      error={!!roundOffError}
      helperText={roundOffError}
      fullWidth
      disabled={!canEdit || isBillPosted || hasIrn}
      sx={{ pr: 2 }}
      InputProps={{
        endAdornment:
          canEdit && !isBillPosted && !hasIrn ? (
            <Tooltip title="Round off to nearest number">
              <IconButton onClick={handleRoundOffToNearest} edge="end" size="small">
                <Calculate fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : undefined,
      }}
    />
  );

  return (
    <>
      {loading && <PageLoader />}

      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity as AlertColor}
          closeNotification={closeNotification}
          autoHideDuration={5000}
        />
      )}

      {/* {pageResponse?.formattedTotalCost && (
        <>
          <Grid container justifyContent="space-between" alignItems="center" mb={2} px={1}>
            <Grid item xs={12}>
              <Typography variant={screenSizeDownMd ? 'subtitle1' : 'h6'}>
                {strings.tabs_text_actual_revenue}:{' '}
                <span className="text-success">{pageResponse.formattedTotalCost}</span>
              </Typography>
            </Grid>
          </Grid>
          <Divider sx={{ mb: 2 }} />
        </>
      )} */}

      {!pageResponse?.formattedTotalCost && <PageLoader />}

      <Grid item container>
        {canEdit || bookingLineItem.actualRevenue !== null ? (
          <>
            <Grid container spacing={1} xs={12}>
              <Form
                hasError={hasError}
                ref={formRef as RefObject<Form>}
                model={ActualRevenueForm(
                  strings,
                  !bookingLineItem.deleted &&
                    isWithinActualizationWindow &&
                    !isBillPosted &&
                    !actualizationFinalized &&
                    !hasIrn,
                  isTransferToRoom,
                  serviceChargeDropdown,
                  serviceCharges,
                  hasSplitRevenuePrivilege,
                  revenueOwnerList,
                  bookedServiceChargePercent,
                  excessGuestCount,
                  extraGuestRateText,
                  <VenueChargesTable
                    preview={preview}
                    pageResponse={pageResponse}
                    bookingLineItem={bookingLineItem}
                    pastBooking={canEdit && !isBillPosted}
                    editable={
                      !bookingLineItem.deleted &&
                      isWithinActualizationWindow &&
                      !isBillPosted &&
                      !actualizationFinalized &&
                      !hasIrn
                    }
                    getFieldValue={field => formRef.current?.getFormData().formData?.[field]}
                    onFieldChange={(field, value) =>
                      formRef.current?.handleChange(value, field, { error: false, message: '' })
                    }
                  />,
                  tdsField,
                  roundOffField,
                )}
                values={pageResponse}
                onChange={handleFormOnChange}
              />
            </Grid>

            <Grid item xs={12} sx={{ mt: 2, mb: 2 }}>
              <AddEditBookingAddOn
                pastBooking={canEdit && !isBillPosted}
                editedData={bookedAddOns}
                previewAddOns={preview?.calculations?.addOns}
                previewVariances={preview?.variance?.addOns}
                setData={(val: any) => {
                  setBookedAddOns(val);
                  markDirty();
                }}
                dropdownData={createDropdownData(pageResponse?.addOns, ['id', 'name'])}
                taxGroupDropdownData={createDropdownData(pageResponse?.taxGroups, ['id', 'name'])}
                noGuest={noGuest}
                addOnCategories={addOnCategories}
              />
            </Grid>

            <Grid item xs={12} sx={{ mb: 2 }}>
              <AddEditOtherItems
                pastBooking={canEdit && !isBillPosted}
                editedData={otherItems}
                previewItems={preview?.calculations?.otherItems}
                previewVariances={preview?.variance?.otherItems}
                setData={(val: any) => {
                  setOtherItems(val);
                  markDirty();
                }}
                dropdownData={createDropdownData(pageResponse?.taxGroups, ['id', 'name'])}
                noGuest={noGuest}
                addOnCategories={addOnCategories}
                isAng={false}
              />
            </Grid>
            <Grid item xs={12} sx={{ mb: 2 }}>
              <AddEditDiscount
                pastBooking={canEdit && !isBillPosted}
                editedData={discounts}
                previewDiscounts={preview?.calculations?.discounts}
                setData={(val: any) => {
                  setDiscounts(val);
                  markDirty();
                }}
                addOnCategories={discountCategories}
              />
            </Grid>
            {userPrivileges?.includes(privileges.add_ang_details) && (
              <Grid item xs={12} sx={{ mb: 2 }}>
                <AddEditOtherItems
                  isAng={true}
                  pastBooking={canEdit}
                  editedData={angItems}
                  setData={(val: any) => {
                    setAngItems(val);
                    markDirty();
                  }}
                  dropdownData={createDropdownData(pageResponse?.taxCodes, ['id', 'name'])}
                  noGuest={noGuest}
                  addOnCategories={addOnCategories}
                />
              </Grid>
            )}
          </>
        ) : (
          <Grid item xs={12}>
            <Alert severity="warning">{strings.no_actualization_added_text}</Alert>
          </Grid>
        )}

        {showUpdateReason && (
          <Grid item xs={12} marginTop={2}>
            <TextField
              label={`${strings.booking_details_actual_revenue_update_reason} *`}
              variant="outlined"
              multiline
              value={updateReason}
              onChange={(e: any) => {
                const val = e.target.value;
                setUpdateReason(val);
                setUpdateReasonError(!val);
              }}
              error={updateReasonError ?? !regex.string.test(updateReason)}
              helperText={
                updateReasonError
                  ? strings.requiredText
                  : !regex.string.test(updateReason)
                  ? strings.validateStringErrMsg
                  : ''
              }
              minRows={2}
              maxRows={5}
              fullWidth
            />
          </Grid>
        )}

        {(showSaveButton ||
          showVoidBillButton ||
          showSummaryButton ||
          showDownloadButton ||
          showSendEmailButton ||
          showValidateEInvoiceButton ||
          showGenerateIrnButton ||
          showDownloadEInvoiceButton ||
          showCancelIrnButton ||
          showPostBillButton ||
          showVoidBillButton ||
          hasIrn) && (
          <Grid item xs={12} sx={{ mt: 2 }}>
            <Stack spacing={0.75} sx={{ width: '100%' }}>
              <Stack
                direction="row"
                spacing={1}
                useFlexGap
                flexWrap="wrap"
                alignItems="center"
                justifyContent="space-between"
                sx={{ width: '100%', gap: 1 }}>
                {(showSaveButton || showSummaryButton || showDownloadButton || showSendEmailButton) && (
                  <Box
                    sx={
                      screenSizeDownSm
                        ? { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1, width: '100%' }
                        : { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }
                    }>
                    {showSaveButton && (
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<SaveOutlined fontSize="small" />}
                        sx={{
                          ...actionButtonSx,
                          ...(isDirty && {
                            boxShadow: '0 0 0 2px rgba(25,118,210,0.25)',
                          }),
                        }}
                        disabled={
                          loading || !regex.string.test(updateReason) || !!roundOffError || !!tdsPercentageError
                        }
                        onClick={handleAddEdit}>
                        {strings.common_button_save}
                        {isDirty ? ` *` : ''}
                      </Button>
                    )}

                    {showDownloadButton && (
                      <>
                        {showSplitBillDownload ? (
                          <>
                            <ButtonGroup variant="outlined" size="small" sx={actionButtonSx} disabled={loading}>
                              <Button
                                startIcon={<ReceiptLong fontSize="small" />}
                                sx={{ flex: 1, minWidth: 0 }}
                                onClick={() => runWithSaveIfDirty(() => downloadInvoice('CONSOLIDATED'))}>
                                {downloadLabel}
                              </Button>
                              <Button
                                size="small"
                                aria-label="select bill view type"
                                onClick={e => setBillViewAnchorEl(e.currentTarget)}>
                                <ArrowDropDown fontSize="small" />
                              </Button>
                            </ButtonGroup>
                            <Button
                              variant="outlined"
                              size="small"
                              sx={{
                                minWidth: 0,
                                height: 36,
                                px: 1,
                                fontSize: 12,
                                fontWeight: 600,
                                lineHeight: 1,
                                textTransform: 'none',
                              }}
                              disabled={loading}
                              onClick={() => runWithSaveIfDirty(() => downloadInvoice('CONSOLIDATED', undefined, 'DOCX'))}>
                              DOCX
                            </Button>
                            <Menu
                              anchorEl={billViewAnchorEl}
                              open={Boolean(billViewAnchorEl)}
                              onClose={() => setBillViewAnchorEl(null)}>
                              <MenuItem disabled sx={{ opacity: 1, pointerEvents: 'none' }}>
                                <Typography
                                  variant="caption"
                                  sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: 0.4 }}>
                                  Invoice Views
                                </Typography>
                              </MenuItem>
                              <MenuItem
                                onClick={() => {
                                  setBillViewAnchorEl(null);
                                  runWithSaveIfDirty(() => downloadInvoice(consolidatedBillViewType.value));
                                }}>
                                <ListItemIcon sx={{ minWidth: 34 }}>
                                  {getBillMenuIcon(consolidatedBillViewType.value)}
                                </ListItemIcon>
                                <ListItemText primary="Consolidated Bill" primaryTypographyProps={{ fontSize: 14 }} />
                              </MenuItem>

                              {(categoryBillViewTypes.length > 0 || billDateOptions.length > 0) && <Divider />}

                              {categoryBillViewTypes.length > 0 && (
                                <MenuItem disabled sx={{ opacity: 1, pointerEvents: 'none' }}>
                                  <Typography
                                    variant="caption"
                                    sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: 0.4 }}>
                                    Category Bills
                                  </Typography>
                                </MenuItem>
                              )}

                              {categoryBillViewTypes.map(option => (
                                <MenuItem
                                  key={option.value}
                                  onClick={() => {
                                    setBillViewAnchorEl(null);
                                    runWithSaveIfDirty(() => downloadInvoice(option.value));
                                  }}>
                                  <ListItemIcon sx={{ minWidth: 34 }}>{getBillMenuIcon(option.value)}</ListItemIcon>
                                  <ListItemText primary={option.label} primaryTypographyProps={{ fontSize: 14 }} />
                                </MenuItem>
                              ))}

                              {categoryBillViewTypes.length > 0 && billDateOptions.length > 0 && <Divider />}

                              {billDateOptions.length > 0 && (
                                <MenuItem disabled sx={{ opacity: 1, pointerEvents: 'none' }}>
                                  <Typography
                                    variant="caption"
                                    sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: 0.4 }}>
                                    Bills by Date
                                  </Typography>
                                </MenuItem>
                              )}

                              {billDateOptions.map(option => (
                                <MenuItem
                                  key={option.value}
                                  onClick={() => {
                                    setBillViewAnchorEl(null);
                                    runWithSaveIfDirty(() => downloadInvoice('CONSOLIDATED', option.value));
                                  }}>
                                  <ListItemIcon sx={{ minWidth: 34 }}>
                                    {getBillMenuIcon(option.value, true)}
                                  </ListItemIcon>
                                  <ListItemText primary={option.label} primaryTypographyProps={{ fontSize: 14 }} />
                                </MenuItem>
                              ))}
                            </Menu>
                          </>
                        ) : (
                          <>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<ReceiptLong fontSize="small" />}
                              sx={actionButtonSx}
                              disabled={loading}
                              onClick={() => runWithSaveIfDirty(() => downloadInvoice('CONSOLIDATED'))}>
                              {downloadLabel}
                            </Button>
                            <Button
                              variant="outlined"
                              size="small"
                              sx={{
                                minWidth: 0,
                                height: 36,
                                px: 1,
                                fontSize: 12,
                                fontWeight: 600,
                                lineHeight: 1,
                                textTransform: 'none',
                                ...(screenSizeDownSm ? { width: '100%' } : {}),
                              }}
                              disabled={loading}
                              onClick={() =>
                                runWithSaveIfDirty(() => downloadInvoice('CONSOLIDATED', undefined, 'DOCX'))
                              }>
                              DOCX
                            </Button>
                          </>
                        )}
                      </>
                    )}

                    {showSummaryButton && (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<ListAlt fontSize="small" />}
                        sx={actionButtonSx}
                        onClick={() => runWithSaveIfDirty(handleCheckSummary)}>
                        {strings.actualizationSummary ?? 'View Summary'}
                      </Button>
                    )}

                    {showSendEmailButton && (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Favorite fontSize="small" />}
                        sx={actionButtonSx}
                        onClick={() => runWithSaveIfDirty(() => setOpenEmailDrawer(true))}>
                        {strings.actualization_send_thank_you}
                      </Button>
                    )}
                  </Box>
                )}

                {(showFinalizeActualization ||
                  showReopenActualization ||
                  showPostBillButton ||
                  showVoidBillButton ||
                  showValidateEInvoiceButton ||
                  showGenerateIrnButton ||
                  showDownloadEInvoiceButton ||
                  showCancelIrnButton) &&
                  pageResponse.id && (
                    <Stack
                      direction="row"
                      spacing={1}
                      useFlexGap
                      flexWrap="wrap"
                      alignItems="center"
                      justifyContent="flex-end"
                      sx={{ flexShrink: 0, ml: 'auto' }}>
                      {showFinalizeActualization && (
                        <Button
                          variant="outlined"
                          size="small"
                          disabled={finalizingActualization}
                          startIcon={<LockOutlined fontSize="small" />}
                          sx={{
                            ...actionButtonSx,
                            color: '#800080',
                            bgcolor: 'transparent',
                            borderColor: '#800080',
                            '&:hover': {
                              bgcolor: 'rgba(128, 0, 128, 0.08)',
                              borderColor: '#6A1B9A',
                            },
                            '&.Mui-disabled': {
                              color: 'rgba(128, 0, 128, 0.4)',
                              borderColor: 'rgba(128, 0, 128, 0.3)',
                            },
                          }}
                          onClick={() => setFinalizeModalOpen(true)}>
                          {strings.finalize_actualization_button}
                        </Button>
                      )}
                      {showReopenActualization && (
                        <Button
                          variant="outlined"
                          size="small"
                          disabled={reopeningActualization}
                          startIcon={<LockOpenOutlined fontSize="small" />}
                          sx={{
                            ...actionButtonSx,
                            color: '#800080',
                            bgcolor: 'transparent',
                            borderColor: '#800080',
                            '&:hover': {
                              bgcolor: 'rgba(128, 0, 128, 0.08)',
                              borderColor: '#6A1B9A',
                            },
                            '&.Mui-disabled': {
                              color: 'rgba(128, 0, 128, 0.4)',
                              borderColor: 'rgba(128, 0, 128, 0.3)',
                            },
                          }}
                          onClick={() => setReopenModalOpen(true)}>
                          {strings.reopen_actualization_button}
                        </Button>
                      )}
                      {showPostBillButton && (
                        <Button
                          variant="contained"
                          size="small"
                          color="success"
                          disabled={postingBill}
                          startIcon={<CloudUpload fontSize="small" />}
                          sx={actionButtonSx}
                          onClick={() => runWithSaveIfDirty(() => setPostBillModalOpen(true))}>
                          {strings.billPostText ?? 'Post Bill'}
                        </Button>
                      )}
                      {showValidateEInvoiceButton && (
                        <Button
                          variant="outlined"
                          size="small"
                          disabled={loading}
                          startIcon={<ReceiptLong fontSize="small" sx={{ color: cleartaxAccentColor }} />}
                          sx={generateIrnButtonSx}
                          onClick={() => void validateEInvoice()}>
                          {strings.validateEInvoiceText}
                        </Button>
                      )}
                      {showGenerateIrnButton && (
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<AccountBalance fontSize="small" sx={{ color: cleartaxAccentColor }} />}
                          sx={generateIrnButtonSx}
                          onClick={() => setGenerateIrnModalOpen(true)}>
                          {strings.postClearTaxText}
                        </Button>
                      )}
                      {showDownloadEInvoiceButton && (
                        <Button
                          variant="outlined"
                          size="small"
                          disabled={loading}
                          startIcon={<ReceiptLong fontSize="small" sx={{ color: cleartaxAccentColor }} />}
                          sx={generateIrnButtonSx}
                          onClick={() => void downloadEInvoicePdf()}>
                          {strings.downloadEInvoicePdfText}
                        </Button>
                      )}
                      {showCancelIrnButton && (
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<DoDisturbOn fontSize="small" sx={{ color: cancelIrnColor }} />}
                          sx={cancelIrnButtonSx}
                          onClick={() => setCancelIrnDialogOpen(true)}>
                          {strings.cancelIrnText}
                        </Button>
                      )}
                      {showVoidBillButton && (
                        <Button
                          variant="contained"
                          size="small"
                          color="error"
                          disabled={voidingBill}
                          startIcon={<DoDisturbOn fontSize="small" />}
                          sx={actionButtonSx}
                          onClick={handleVoidBillClick}>
                          {strings.voidBillText}
                        </Button>
                      )}
                    </Stack>
                  )}
              </Stack>

              {hasIrn && (
                <Stack
                  direction="row"
                  spacing={0.5}
                  alignItems="center"
                  justifyContent="flex-end"
                  sx={{ width: '100%', minWidth: 0 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={600}
                    sx={{ flexShrink: 0, fontSize: '0.7rem' }}>
                    {strings.irnNumberLabel}
                  </Typography>
                  <Typography
                    variant="caption"
                    component="span"
                    fontFamily="monospace"
                    sx={{
                      fontSize: '0.7rem',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxWidth: { xs: '55vw', sm: 400 },
                      bgcolor: 'grey.100',
                      px: 0.75,
                      py: 0.25,
                      borderRadius: 0.5,
                      lineHeight: 1.4,
                    }}>
                    {pageResponse.irnNumber}
                  </Typography>
                  <IconButton
                    size="small"
                    aria-label={strings.irnCopyText}
                    onClick={() => void copyIrnToClipboard()}
                    sx={{ flexShrink: 0, p: 0.25 }}>
                    <ContentCopy sx={{ fontSize: 14 }} />
                  </IconButton>
                </Stack>
              )}
            </Stack>
          </Grid>
        )}

        {actualizationInfo && (
          <Grid item xs={12} sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', mb: 0.5 }}>
              <Box sx={{ width: 'fit-content', maxWidth: '100%' }}>
                <ReportInsightBanner text={actualizationInfo.text} style={actualizationInfo.style} centered />
              </Box>
            </Box>
          </Grid>
        )}

        <ConfirmModal
          open={postBillModalOpen}
          closeModal={() => setPostBillModalOpen(false)}
          message={formatPmsMessage(strings.postBillConfirmMessage)}
          handleYes={() => void handlePostBillConfirm()}
          title={formatPmsMessage(strings.postBillConfirmTitle)}
          confirmLabel={strings.billPostText}
          cancelLabel={strings.cancelText}
          tone="warning"
          loading={postingBill}
        />

        <ConfirmModal
          open={finalizeModalOpen}
          closeModal={() => setFinalizeModalOpen(false)}
          message={strings.finalize_actualization_confirm_message}
          handleYes={handleFinalizeActualization}
          title={strings.finalize_actualization_confirm_title}
          confirmLabel={strings.finalize_actualization_button}
          cancelLabel={strings.cancelText}
          tone="finalize"
          loading={finalizingActualization}
        />

        <ConfirmModal
          open={reopenModalOpen}
          closeModal={() => setReopenModalOpen(false)}
          message={strings.reopen_actualization_confirm_message}
          handleYes={handleReopenActualization}
          title={strings.reopen_actualization_confirm_title}
          confirmLabel={strings.reopen_actualization_button}
          cancelLabel={strings.cancelText}
          tone="finalize"
          loading={reopeningActualization}
        />

        <ConfirmModal
          open={generateIrnModalOpen}
          closeModal={() => setGenerateIrnModalOpen(false)}
          message={strings.generateIrnConfirmMessage}
          handleYes={() => void handleGenerateIrnConfirm()}
          title={strings.generateIrnConfirmTitle}
          confirmLabel={strings.postClearTaxText}
          cancelLabel={strings.cancelText}
          tone="warning"
          loading={generatingIrn}
        />

        {eInvoiceErrors && (
          <EInvoiceErrorsCard
            title={eInvoiceErrors.title}
            errors={eInvoiceErrors.errors}
            onDismiss={clearEInvoiceErrorCard}
          />
        )}

        <Dialog
          open={cancelIrnDialogOpen}
          onClose={() => setCancelIrnDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 2, boxShadow: '0 8px 32px rgba(0,0,0,0.12)' } }}>
          <DialogTitle
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              pb: 1,
              color: 'error.main',
              fontWeight: 600,
              fontSize: '1.25rem',
            }}>
            <DoDisturbOn sx={{ fontSize: 28, color: 'error.main' }} />
            {strings.cancelIrnConfirmTitle}
          </DialogTitle>
          <DialogContent sx={{ pt: 2, pb: 1 }}>
            <DialogContentText sx={{ fontSize: '0.95rem', color: 'text.primary', mb: 2 }}>
              {strings.cancelIrnConfirmMessage}
            </DialogContentText>
            <Stack spacing={2}>
              <TextField
                select
                label={strings.cancelIrnReasonLabel}
                value={cancelIrnReason}
                onChange={e => setCancelIrnReason(e.target.value)}
                size="small"
                fullWidth
                SelectProps={{ native: true }}>
                <option value="1">1 — Duplicate</option>
                <option value="2">2 — Data entry mistake</option>
                <option value="3">3 — Order Cancelled</option>
                <option value="4">4 — Others</option>
              </TextField>
              <TextField
                label={strings.cancelIrnRemarksLabel}
                value={cancelIrnRemarks}
                onChange={e => setCancelIrnRemarks(e.target.value)}
                size="small"
                fullWidth
                inputProps={{ maxLength: 100 }}
                placeholder="Enter cancellation remarks"
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 1, gap: 1 }}>
            <Button
              onClick={() => setCancelIrnDialogOpen(false)}
              variant="outlined"
              sx={{ minWidth: 100, borderRadius: 1, textTransform: 'none', fontWeight: 500 }}>
              {strings.cancelText}
            </Button>
            <Button
              onClick={handleCancelIrn}
              variant="outlined"
              disabled={cancellingIrn}
              startIcon={<DoDisturbOn sx={{ color: cancelIrnColor }} />}
              sx={{
                minWidth: 120,
                borderRadius: 1,
                textTransform: 'none',
                fontWeight: 500,
                boxShadow: 'none',
                bgcolor: '#fff',
                color: cancelIrnColor,
                borderColor: cancelIrnColor,
                '&:hover': {
                  bgcolor: 'rgba(159, 7, 18, 0.08)',
                  borderColor: cancelIrnColor,
                },
              }}>
              {cancellingIrn ? 'Cancelling...' : strings.cancelIrnText}
            </Button>
          </DialogActions>
        </Dialog>

        <Divider sx={{ my: 3 }} />

        <DrawerMenu
          width={getDrawerWidth(screenSizeDownSm, screenSizeDownMd, screenSizeUpLg)}
          open={Object.keys(costingDrawerPageResponse).length > 0}
          handleClose={() => setCostingDrawerPageResponse({})}
          closeButton
          title={strings.actualizationSummary}>
          <CostingComponent
            pageResponse={costingDrawerPageResponse}
            downloadAction={
              canDownload && pageResponse.id
                ? {
                    onClick: () => runWithSaveIfDirty(() => downloadInvoice('CONSOLIDATED')),
                    loading,
                    label: 'Download Invoice PDF',
                  }
                : undefined
            }
            docxAction={
              canDownload && pageResponse.id
                ? {
                    onClick: () => runWithSaveIfDirty(() => downloadInvoice('CONSOLIDATED', undefined, 'DOCX')),
                    loading,
                    label: 'Download Invoice DOCX',
                  }
                : undefined
            }
          />
        </DrawerMenu>

        <DrawerMenu
          width={getDrawerWidth(screenSizeDownSm, screenSizeDownMd, screenSizeUpLg)}
          open={openEmailDrawer}
          handleClose={() => setOpenEmailDrawer(false)}
          closeButton
          flexColumn
          noPadding
          title={strings.actualization_send_email_title}>
          <SendEmail
            bookingLineItem={bookingLineItem}
            invoice={{
              referenceNo: pageResponse.referenceNo,
              formattedTotalCost: pageResponse.formattedTotalCost,
            }}
            setOpenDrawer={setOpenEmailDrawer}
            handleCatch={handleCatch}
          />
        </DrawerMenu>

        <Dialog
          open={openVoidBillDialog}
          onClose={() => setOpenVoidBillDialog(false)}
          aria-labelledby="void-bill-dialog-title"
          aria-describedby="void-bill-dialog-description"
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            },
          }}>
          <DialogTitle
            id="void-bill-dialog-title"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              pb: 1,
              color: 'error.main',
              fontWeight: 600,
              fontSize: '1.25rem',
            }}>
            <Warning sx={{ fontSize: 28, color: 'error.main' }} />
            {strings.voidBillText}
          </DialogTitle>
          <DialogContent sx={{ pt: 2, pb: 3 }}>
            <DialogContentText
              id="void-bill-dialog-description"
              sx={{
                fontSize: '1rem',
                lineHeight: 1.6,
                color: 'text.primary',
                mb: 2,
              }}>
              Are you sure you want to void this bill? This action cannot be undone and will permanently mark the bill
              as voided.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 1, gap: 1 }}>
            <Button
              onClick={() => setOpenVoidBillDialog(false)}
              variant="outlined"
              disabled={voidingBill}
              sx={{
                minWidth: 100,
                borderRadius: 1,
                textTransform: 'none',
                fontWeight: 500,
              }}>
              Cancel
            </Button>
            <Button
              onClick={() => void voidBill()}
              color="error"
              variant="contained"
              autoFocus
              disabled={voidingBill}
              startIcon={<DoDisturbOn />}
              sx={{
                minWidth: 120,
                borderRadius: 1,
                textTransform: 'none',
                fontWeight: 500,
                boxShadow: 'none',
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(211, 47, 47, 0.3)',
                },
              }}>
              {voidingBill ? 'Voiding…' : strings.voidBillText}
            </Button>
          </DialogActions>
        </Dialog>

        <UnsavedChangesModal
          open={unsavedDialog.open}
          onClose={closeUnsavedDialog}
          onSave={() => void handleSaveAndContinue()}
          onDiscard={() => void handleDiscardAndContinue()}
          loading={unsavedSaving}
        />
      </Grid>

      <Divider sx={{ my: 2 }} />
    </>
  );
};

export default ActualRevenue;
